/** Activity, Health and usage, and Quality (ADR-0066): native, plain-language Observe pages. */
export default {
  en: {
    activity: {
      title: "Activity",
      purpose: "Who asked what: live and recent requests, which agent answered, and how it went.",
      scopeOwn: "You see your own requests. People who run, manage or check the platform for your organisation see everyone's.",
      scopeTenant: "Everyone's requests in your organisation.",
      filters: {
        label: "Filter the requests",
        status: "Status",
        anyStatus: "Any status",
        agent: "Agent",
        anyAgent: "Any agent",
        person: "Person",
        anyPerson: "Anyone",
        range: "Time",
        refresh: "Refresh",
        live: "Updates by itself every 15 seconds.",
      },
      range: { h1: "Last hour", d1: "Last 24 hours", d7: "Last 7 days", d30: "Last 30 days" },
      status: {
        running: "Running",
        waiting: "Waiting for a person",
        completed: "Answered",
        refused: "Refused",
        failed: "Failed",
        cancelled: "Stopped",
      },
      asked: "<person/> asked <agent/> “{{excerpt}}”",
      askedNoText: "<person/> asked <agent/> something",
      someone: "Someone",
      meta: {
        running: "for {{time}} so far",
        waiting: "for {{who}} · {{time}}",
        waitingAnyone: "for {{time}}",
        completed: "in {{took}} · {{ago}} ago",
        refused: "{{ago}} ago",
        failed: "{{ago}} ago",
        cancelled: "{{ago}} ago",
      },
      why: "Why:",
      open: "Open",
      openRequest: "Open the request from {{name}}",
      more: "Show more",
      count: "{{shown}} of {{total}} requests",
      truncated: "Only the most recent requests of this period were checked; choose a shorter time to see older ones.",
      empty: "No requests match these filters.",
      emptyHint: "Try a longer time or clear a filter.",
      error: "The activity could not be loaded.",
    },
    health: {
      alertSomething: "A part of the platform",
      alertAnAgent: "An agent",
      alertAQueue: "a queue",
      alertText: {
        TargetDown: "{{job}} is not answering.",
        TemporalHighPriorityBacklog: "Urgent work is queuing up in {{queue}}.",
        TemporalActivityFailures: "Steps keep failing and are being retried.",
        TelemetryExportFailing: "Monitoring data is not being saved.",
        ApprovalSlaBreached: "Approvals in {{queue}} are past their deadline.",
        ApprovalQueueOverdue: "Approvals in {{queue}} are overdue.",
        ApprovalDecisionSlow: "Approval decisions are taking longer than 4 hours.",
        AgentRunaway: "{{agent}} hit one of its limits and was stopped.",
        AgentRunawayStorm: "{{agent}} keeps going round in circles.",
        AgentCostBudgetStops: "Requests to {{agent}} keep using up their spending limit.",
        CostSpike: "AI spending is much higher than usual.",
        TokenBurnSpike: "{{agent}} is using the AI five times faster than usual.",
        BudgetSoftThresholdExceeded: "A spending limit is nearly used up.",
        BudgetExhausted: "A spending limit is used up.",
        BudgetExhaustionForecast: "A spending limit will run out within a day.",
        ErrorBudgetBurnFast: "{{service}} is failing far more often than allowed.",
        ErrorBudgetBurnSlow: "{{service}} is failing more often than allowed.",
        AgentRunFailureRateHigh: "More than 1 in 10 requests to {{agent}} fail.",
        ServiceLatencyP95High: "{{service}} is slow to answer.",
        ModelLatencyP95High: "The AI model {{model}} is slow to answer.",
        ToolLatencyP95High: "The system {{tool}} is slow to answer.",
        AgentSegmentLatencyP95High: "{{agent}} is slow to answer.",
        PolicyDenialSpike: "Many actions of {{agent}} are being refused by the rules.",
        GatewayPolicyDenials: "Many AI requests are being refused by the rules.",
        IdentityHeaderRejected: "A part of the platform received requests with a forged identity; they were refused.",
        IdentityHeaderProbing: "Many requests with a forged identity were refused.",
        GuardrailBlockSpike: "The guardrails are blocking three times more than yesterday.",
        KillSwitchEngaged: "An emergency stop is on.",
        EgressDenied: "Attempts to reach the internet were blocked.",
        AuditWriteFailures: "Audit records could not be written.",
        CertificateExpiringSoon: "A security certificate expires soon.",
        KeyRotationDue: "A signing key is due to be replaced.",
      },
      title: "Health and usage",
      purpose: "Is it working, and what does it cost? How much is used, and whether anything is about to go wrong.",
      tab: { overview: "Overview", details: "Operations detail", flow: "How requests flow" },
      banner: {
        ok: "Everything is working",
        okBody_one: "The {{count}} part of the platform that reports its state is answering.",
        okBody_other: "All {{count}} parts of the platform are answering.",
        okNoParts: "No problems are reported.",
        degraded: "Something needs attention",
        down: "There is a serious problem",
        partsDown: "Not answering: {{names}}.",
        alerts: "Active alerts:",
        unknown: "Health checks are not connected",
        unknownBody: "This installation does not report the state of its parts here. Engineers can check the dashboards.",
        error: "The health of the platform could not be checked.",
      },
      part: {
        audit: "Audit records",
        identity: "Sign-in and identities",
        orchestrator: "Agent requests",
        "orchestrator-worker": "Agent workers",
        "temporal-worker-sdk": "Agent workers (workflow link)",
        cases: "Cases and tasks",
        memory: "Memory",
        metering: "Usage and spending limits",
        gateway: "AI model gateway",
        retrieval: "Knowledge search",
        guardrails: "Guardrails",
        docintel: "Document reading",
        "control-plane": "Governance",
        channels: "Channels",
        temporal: "Workflow engine",
        opa: "Rules engine",
        "otel-collector": "Telemetry",
        "egress-proxy": "Internet gateway",
        "tool-filesystem": "Files tool",
        mlflow: "Prompt library",
        "speech-local": "Local voice (speech on our own servers)",
      },
      optional: {
        title: "Parts the platform can run without",
        mlflow: {
          up: "Running.",
          down: "Not running: prompts can't be shown or saved in the Builder. An operator can start it with `docker compose start mlflow`.",
          absent: "Not checked here. If the Builder's Prompts tab says the library isn't running, an operator can start it with `docker compose start mlflow`.",
        },
        "speech-local": {
          up: "Running: private conversations can be listened to and spoken on our own servers.",
          down: "Local voice is off. An operator can switch it on with `make voice-local-up` (needs about 1 GB of memory). Until then, private voice conversations are refused.",
          absent: "Local voice is off. An operator can switch it on with `make voice-local-up` (needs about 1 GB of memory). Until then, private voice conversations are refused.",
        },
      },
      cards: {
        requests: {
          title: "Requests this week",
          up: "Up {{pct}} on last week.",
          down: "Down {{pct}} on last week.",
          same: "About the same as last week.",
          noPrevious: "There were no requests the week before.",
          none: "No requests yet this week.",
          mostly: "Mostly {{agent}}.",
          trend: "Requests per day over the last 7 days",
          action: "See all activity",
        },
        waiting: {
          title: "Waiting for people",
          none: "Nobody is waiting for a decision.",
          oldest: "The oldest has waited {{time}}.",
          approvers: "Who can answer: {{roles}}.",
          action: "See who is waiting",
        },
        budget: {
          title: "AI spending limit this month",
          none: "No monthly AI spending limit is set.",
          used: "{{used}} of {{limit}} used.",
          reach: "At this pace you will reach the limit on {{date}}.",
          within: "At this pace you will stay within the limit.",
          over: "The limit has been reached; new requests that cost money are stopped.",
          action: "Review spending",
          setAction: "Set a spending limit",
          meter: "Spending limit used",
        },
        failed: {
          title: "Requests that failed",
          rate: "{{n}} of {{total}}.",
          none: "No request failed this week.",
          common: "Most common reason: {{reason}}",
          action: "See them",
        },
        unavailable: "Not available to you.",
        error: "Could not be loaded.",
      },
      engineers: "Engineers: detailed dashboards, workflow history and experiments are under <tools>Engineering tools</tools>.",
    },
    quality: {
      title: "Quality",
      purpose: "Test results: are the agents and AI models good enough? Each is tested before it can go further.",
      agents: "Agents",
      models: "AI models",
      modelName: "{{name}} (AI model)",
      chip: {
        ready_test: "Ready for Testing",
        ready_staging: "Ready for the final check",
        ready_prod: "Ready for Live",
        failed: "Failed a test",
        untested: "Not tested yet",
        devOnly: "Development only",
      },
      passed: "passed",
      notReached: "not reached",
      goalMin: "goal {{goal}}",
      goalMax: "goal at most {{goal}}",
      metric: {
        task_success: "Answers correctly",
        groundedness: "Sticks to your documents",
        policy_violations: "Times it broke a rule",
        attack_success_rate: "Resists misuse attempts",
        score: "As good as the previous version",
        pass_rate: "Works as described",
        instruction_following: "Follows instructions",
        structured_output: "Fills in forms correctly",
        tool_call_accuracy: "Uses tools correctly",
        refusal_correctness: "Refuses what it should",
        msa_comprehension: "Understands Arabic",
        gulf_comprehension: "Understands Gulf Arabic",
        rtl_bidi: "Writes mixed Arabic and English correctly",
        arabic_numerals: "Handles Arabic numbers",
        max_group_gap: "Treats groups of people alike",
      },
      suite: {
        "agent-golden": "Test questions",
        "agent-redteam": "Misuse attempts",
        "prompt-regression": "Comparison with the previous version",
        "tool-contract": "Contract test",
        "model-capability": "Model abilities",
        "arabic-quality": "Arabic",
        fairness: "Fairness",
      },
      tested: "{{suite}} · tested {{ago}} ago · version {{version}}",
      olderVersion: "These results are for version {{version}}; the newest version {{latest}} has not been tested yet.",
      untestedAgent: "This agent has <b>not been tested</b> yet. It can be tried in Development and Testing, but it cannot reach the final check or Live until it passes its tests.",
      untestedModel: "This model has <b>not been tested</b> yet, so it can't be used in Testing or Live.",
      nextAgent: "<b>Next:</b> run its test questions and misuse tests. When they pass, it can move on in Governance › Releases.",
      untestedAgents: "Agents marked <b>Not tested yet</b> can be tried in Development and Testing, but cannot reach the final check or Live until they pass their tests. <b>Next:</b> open their tests and run the test questions and misuse tests; then they can move on in Governance › Releases.",
      untestedModels: "AI models marked <b>Development only</b> have not been tested, so they can't be used in Testing or Live. <b>Next:</b> run the model tests (about 10 minutes); if they pass, a model admin can approve the model.",
      search: "Find an agent or model",
      hideRetired: "Hide retired agents",
      nextModel: "<b>To use it beyond development:</b> run the model tests (about 10 minutes). If it passes, a model admin can approve it.",
      failedNext: "Fix what was not reached and run the tests again; until then it cannot move on.",
      runAgentTests: "Open its tests",
      runModelTests: "Run the model tests",
      howTitle: "Running the model tests",
      howBody: "An engineer starts the model tests with the evaluation tool. The results appear on this page by themselves when they are done.",
      noAgents: "No agents yet.",
      noModels: "No AI models are registered yet.",
      error: "The quality results could not be loaded.",
      engineers: "Engineers: the full experiment history is under <tools>Engineering tools › Experiments</tools>.",
      tech: { name: "Technical name", version: "Newest version", digest: "Version fingerprint (SHA-256)", run: "Test run reference: {{suite}}" },
    },
    roles: {
      approver: "approvers",
      "finance-approver": "finance approvers",
      "release-approver": "release approvers",
      reviewer: "reviewers",
      "case-worker": "case workers",
      "case-supervisor": "case supervisors",
      "channel-agent": "channel agents",
    },
    ago: "{{time}} ago",
    list: "{{items}}",
  },
  ar: {
    activity: {
      title: "النشاط",
      purpose: "الطلبات الجارية والأخيرة: من سأل أيّ وكيل عن ماذا، وكيف انتهى الأمر.",
      scopeOwn: "ترى طلباتك أنت. من يشغّلون المنصة لجهتك أو يديرونها أو يدققونها يرون طلبات الجميع.",
      scopeTenant: "طلبات الجميع في مؤسستك.",
      filters: {
        label: "تصفية الطلبات",
        status: "الحالة",
        anyStatus: "أي حالة",
        agent: "الوكيل",
        anyAgent: "أي وكيل",
        person: "الشخص",
        anyPerson: "أي شخص",
        range: "الوقت",
        refresh: "تحديث",
        live: "تتحدّث الصفحة تلقائيًا كل 15 ثانية.",
      },
      range: { h1: "آخر ساعة", d1: "آخر 24 ساعة", d7: "آخر 7 أيام", d30: "آخر 30 يومًا" },
      status: {
        running: "قيد التنفيذ",
        waiting: "بانتظار شخص",
        completed: "تمت الإجابة",
        refused: "مرفوض",
        failed: "تعذّر",
        cancelled: "أُوقف",
      },
      asked: "سأل <person/> الوكيل <agent/>: «{{excerpt}}»",
      askedNoText: "أرسل <person/> طلبًا إلى <agent/>",
      someone: "شخص ما",
      meta: {
        running: "منذ {{time}}",
        waiting: "{{who}} · منذ {{time}}",
        waitingAnyone: "منذ {{time}}",
        completed: "خلال {{took}} · منذ {{ago}}",
        refused: "منذ {{ago}}",
        failed: "منذ {{ago}}",
        cancelled: "منذ {{ago}}",
      },
      why: "السبب:",
      open: "فتح",
      openRequest: "فتح طلب {{name}}",
      more: "عرض المزيد",
      count: "{{shown}} من {{total}} طلبًا",
      truncated: "فُحصت أحدث طلبات هذه الفترة فقط؛ اختر مدة أقصر لرؤية الأقدم.",
      empty: "لا توجد طلبات تطابق هذه التصفية.",
      emptyHint: "جرّب مدة أطول أو أزل أحد عوامل التصفية.",
      error: "تعذّر تحميل النشاط.",
    },
    health: {
      alertSomething: "جزء من المنصة",
      alertAnAgent: "وكيل",
      alertAQueue: "إحدى القوائم",
      alertText: {
        TargetDown: "{{job}} لا يستجيب.",
        TemporalHighPriorityBacklog: "يتراكم العمل العاجل في {{queue}}.",
        TemporalActivityFailures: "تفشل خطوات بشكل متكرر وتُعاد محاولتها.",
        TelemetryExportFailing: "لا تُحفظ بيانات المراقبة.",
        ApprovalSlaBreached: "تجاوزت الموافقات في {{queue}} موعدها.",
        ApprovalQueueOverdue: "الموافقات في {{queue}} متأخرة.",
        ApprovalDecisionSlow: "تستغرق قرارات الموافقة أكثر من 4 ساعات.",
        AgentRunaway: "بلغ {{agent}} أحد حدوده فأُوقف.",
        AgentRunawayStorm: "يدور {{agent}} في حلقة متكررة.",
        AgentCostBudgetStops: "تستنفد طلبات {{agent}} حد إنفاقها باستمرار.",
        CostSpike: "إنفاق الذكاء الاصطناعي أعلى بكثير من المعتاد.",
        TokenBurnSpike: "يستخدم {{agent}} الذكاء الاصطناعي أسرع بخمس مرات من المعتاد.",
        BudgetSoftThresholdExceeded: "قارب أحد حدود الإنفاق على النفاد.",
        BudgetExhausted: "نفد أحد حدود الإنفاق.",
        BudgetExhaustionForecast: "سينفد أحد حدود الإنفاق خلال يوم.",
        ErrorBudgetBurnFast: "تتعطل {{service}} أكثر بكثير من المسموح.",
        ErrorBudgetBurnSlow: "تتعطل {{service}} أكثر من المسموح.",
        AgentRunFailureRateHigh: "يفشل أكثر من طلب من كل 10 طلبات إلى {{agent}}.",
        ServiceLatencyP95High: "{{service}} بطيئة في الاستجابة.",
        ModelLatencyP95High: "نموذج الذكاء الاصطناعي {{model}} بطيء في الاستجابة.",
        ToolLatencyP95High: "النظام {{tool}} بطيء في الاستجابة.",
        AgentSegmentLatencyP95High: "{{agent}} بطيء في الإجابة.",
        PolicyDenialSpike: "ترفض القواعد كثيرًا من أفعال {{agent}}.",
        GatewayPolicyDenials: "ترفض القواعد كثيرًا من طلبات الذكاء الاصطناعي.",
        IdentityHeaderRejected: "تلقى جزء من المنصة طلبات بهوية مزوّرة ورُفضت.",
        IdentityHeaderProbing: "رُفضت طلبات كثيرة بهوية مزوّرة.",
        GuardrailBlockSpike: "تحجب الضوابط الوقائية ثلاثة أضعاف ما حجبته أمس.",
        KillSwitchEngaged: "الإيقاف الطارئ مفعّل.",
        EgressDenied: "حُجبت محاولات للوصول إلى الإنترنت.",
        AuditWriteFailures: "تعذّر تسجيل سجلات التدقيق.",
        CertificateExpiringSoon: "تنتهي شهادة أمان قريبًا.",
        KeyRotationDue: "حان وقت استبدال مفتاح توقيع.",
      },
      title: "الصحة والاستخدام",
      purpose: "هل يعمل كل شيء، وكم يكلّف؟ حجم الاستخدام، وهل هناك ما يوشك أن يتعطّل.",
      tab: { overview: "نظرة عامة", details: "تفاصيل التشغيل", flow: "كيف تسير الطلبات" },
      banner: {
        ok: "كل شيء يعمل",
        okBody_zero: "لا توجد أجزاء تُبلغ عن حالتها.",
        okBody_one: "الجزء الوحيد الذي يُبلغ عن حالته يستجيب.",
        okBody_two: "جزءا المنصة يستجيبان.",
        okBody_few: "جميع أجزاء المنصة الـ{{count}} تستجيب.",
        okBody_many: "جميع أجزاء المنصة الـ{{count}} تستجيب.",
        okBody_other: "جميع أجزاء المنصة الـ{{count}} تستجيب.",
        okNoParts: "لا توجد مشكلات مُبلَّغ عنها.",
        degraded: "هناك ما يحتاج إلى انتباه",
        down: "هناك مشكلة جسيمة",
        partsDown: "لا يستجيب: {{names}}.",
        alerts: "التنبيهات النشطة:",
        unknown: "فحوص الصحة غير موصولة",
        unknownBody: "لا تُبلغ هذه النسخة عن حالة أجزائها هنا. يمكن للمهندسين مراجعة لوحات المراقبة.",
        error: "تعذّر فحص صحة المنصة.",
      },
      part: {
        audit: "سجلات التدقيق",
        identity: "الدخول والهويات",
        orchestrator: "طلبات الوكلاء",
        "orchestrator-worker": "عمّال الوكلاء",
        "temporal-worker-sdk": "عمّال الوكلاء (ربط سير العمل)",
        cases: "الحالات والمهام",
        memory: "الذاكرة",
        metering: "الاستخدام وحدود الإنفاق",
        gateway: "بوابة نماذج الذكاء الاصطناعي",
        retrieval: "البحث في المعرفة",
        guardrails: "الضوابط الوقائية",
        docintel: "قراءة المستندات",
        "control-plane": "الحوكمة",
        channels: "القنوات",
        temporal: "محرّك سير العمل",
        opa: "محرّك القواعد",
        "otel-collector": "القياس عن بُعد",
        "egress-proxy": "بوابة الإنترنت",
        "tool-filesystem": "أداة الملفات",
        mlflow: "مكتبة الموجّهات",
        "speech-local": "الصوت المحلي (الكلام على خوادمنا)",
      },
      optional: {
        title: "أجزاء يمكن للمنصة العمل بدونها",
        mlflow: {
          up: "تعمل.",
          down: "لا تعمل: لا يمكن عرض الموجّهات أو حفظها في المنشئ. يمكن لأحد المشغّلين تشغيلها بالأمر `docker compose start mlflow`.",
          absent: "لا تُفحص هنا. إذا ذكر تبويب الموجّهات في المنشئ أن المكتبة لا تعمل، يمكن لأحد المشغّلين تشغيلها بالأمر `docker compose start mlflow`.",
        },
        "speech-local": {
          up: "يعمل: يمكن الاستماع إلى المحادثات الخاصة والتحدث فيها على خوادمنا.",
          down: "الصوت المحلي متوقف. يمكن لأحد المشغّلين تشغيله بالأمر `make voice-local-up` (يحتاج نحو 1 غيغابايت من الذاكرة). حتى ذلك الحين تُرفض المحادثات الصوتية الخاصة.",
          absent: "الصوت المحلي متوقف. يمكن لأحد المشغّلين تشغيله بالأمر `make voice-local-up` (يحتاج نحو 1 غيغابايت من الذاكرة). حتى ذلك الحين تُرفض المحادثات الصوتية الخاصة.",
        },
      },
      cards: {
        requests: {
          title: "الطلبات هذا الأسبوع",
          up: "ارتفاع بنسبة {{pct}} عن الأسبوع الماضي.",
          down: "انخفاض بنسبة {{pct}} عن الأسبوع الماضي.",
          same: "قريب من الأسبوع الماضي.",
          noPrevious: "لم تكن هناك طلبات في الأسبوع السابق.",
          none: "لا توجد طلبات بعد هذا الأسبوع.",
          mostly: "أغلبها إلى {{agent}}.",
          trend: "الطلبات يوميًا خلال آخر 7 أيام",
          action: "عرض كل النشاط",
        },
        waiting: {
          title: "بانتظار أشخاص",
          none: "لا أحد ينتظر قرارًا.",
          oldest: "أقدمها ينتظر منذ {{time}}.",
          approvers: "من يمكنه الرد: {{roles}}.",
          action: "عرض من ينتظر",
        },
        budget: {
          title: "حد إنفاق الذكاء الاصطناعي لهذا الشهر",
          none: "لم يُحدَّد حد إنفاق شهري للذكاء الاصطناعي.",
          used: "استُخدم {{used}} من {{limit}}.",
          reach: "بهذا المعدل ستبلغ الحد في {{date}}.",
          within: "بهذا المعدل ستبقى ضمن الحد.",
          over: "بلغت الحد؛ تتوقف الطلبات الجديدة التي لها تكلفة.",
          action: "مراجعة الإنفاق",
          setAction: "تحديد حد إنفاق",
          meter: "المستخدم من حد الإنفاق",
        },
        failed: {
          title: "الطلبات التي تعذّرت",
          rate: "{{n}} من {{total}}.",
          none: "لم يتعذّر أي طلب هذا الأسبوع.",
          common: "السبب الأكثر شيوعًا: {{reason}}",
          action: "عرضها",
        },
        unavailable: "غير متاح لك.",
        error: "تعذّر التحميل.",
      },
      engineers: "للمهندسين: لوحات المراقبة التفصيلية وسجل سير العمل والتجارب موجودة ضمن <tools>الأدوات الهندسية</tools>.",
    },
    quality: {
      title: "الجودة",
      purpose: "هل الوكلاء ونماذج الذكاء الاصطناعي جيدة بما يكفي؟ يُختبر كلٌّ منها قبل أن يتقدّم، وهذه هي النتائج.",
      agents: "الوكلاء",
      models: "نماذج الذكاء الاصطناعي",
      modelName: "{{name}} (نموذج ذكاء اصطناعي)",
      chip: {
        ready_test: "جاهز للاختبار",
        ready_staging: "جاهز للفحص النهائي",
        ready_prod: "جاهز للتشغيل الفعلي",
        failed: "لم يجتز اختبارًا",
        untested: "لم يُختبر بعد",
        devOnly: "للتطوير فقط",
      },
      passed: "ناجح",
      notReached: "لم يتحقق",
      goalMin: "الهدف {{goal}}",
      goalMax: "الهدف {{goal}} كحد أقصى",
      metric: {
        task_success: "يجيب إجابات صحيحة",
        groundedness: "يلتزم بمستنداتك",
        policy_violations: "مرات مخالفة القواعد",
        attack_success_rate: "يقاوم محاولات إساءة الاستخدام",
        score: "بجودة الإصدار السابق",
        pass_rate: "يعمل كما هو موصوف",
        instruction_following: "يتبع التعليمات",
        structured_output: "يملأ النماذج بشكل صحيح",
        tool_call_accuracy: "يستخدم الأدوات بشكل صحيح",
        refusal_correctness: "يرفض ما ينبغي رفضه",
        msa_comprehension: "يفهم العربية",
        gulf_comprehension: "يفهم اللهجة الخليجية",
        rtl_bidi: "يكتب النص العربي والإنجليزي المختلط بشكل صحيح",
        arabic_numerals: "يتعامل مع الأرقام العربية",
        max_group_gap: "يعامل فئات الناس بالتساوي",
      },
      suite: {
        "agent-golden": "أسئلة الاختبار",
        "agent-redteam": "محاولات إساءة الاستخدام",
        "prompt-regression": "المقارنة مع الإصدار السابق",
        "tool-contract": "اختبار العقد",
        "model-capability": "قدرات النموذج",
        "arabic-quality": "اللغة العربية",
        fairness: "الإنصاف",
      },
      tested: "{{suite}} · اختُبر منذ {{ago}} · الإصدار {{version}}",
      olderVersion: "هذه النتائج للإصدار {{version}}؛ أحدث إصدار {{latest}} لم يُختبر بعد.",
      untestedAgent: "هذا الوكيل <b>لم يُختبر</b> بعد. يمكن تجربته في التطوير والاختبار، لكنه لا يصل إلى الفحص النهائي أو التشغيل الفعلي حتى يجتاز اختباراته.",
      untestedModel: "هذا النموذج <b>لم يُختبر</b> بعد، لذا لا يمكن استخدامه في الاختبار أو التشغيل الفعلي.",
      nextAgent: "<b>الخطوة التالية:</b> شغّل أسئلة الاختبار واختبارات إساءة الاستخدام. عند نجاحها يمكن نقله في الحوكمة › الإصدارات.",
      untestedAgents: "الوكلاء الموسومون بـ<b>لم يُختبر بعد</b> يمكن تجربتهم في التطوير والاختبار، لكنهم لا يصلون إلى الفحص النهائي أو التشغيل الفعلي حتى يجتازوا اختباراتهم. <b>الخطوة التالية:</b> افتح اختباراتهم وشغّل أسئلة الاختبار واختبارات إساءة الاستخدام، ثم يمكن نقلهم في الحوكمة › الإصدارات.",
      untestedModels: "نماذج الذكاء الاصطناعي الموسومة بـ<b>التطوير فقط</b> لم تُختبر، لذا لا يمكن استخدامها في الاختبار أو التشغيل الفعلي. <b>الخطوة التالية:</b> شغّل اختبارات النموذج (نحو 10 دقائق)؛ وإذا نجحت يمكن لمسؤول النماذج اعتماده.",
      search: "ابحث عن وكيل أو نموذج",
      hideRetired: "إخفاء الوكلاء المتقاعدين",
      nextModel: "<b>لاستخدامه بعد التطوير:</b> شغّل اختبارات النموذج (نحو 10 دقائق). إذا نجح يمكن لمسؤول النماذج اعتماده.",
      failedNext: "أصلح ما لم يتحقق وأعد تشغيل الاختبارات؛ ولن يتقدّم حتى ذلك الحين.",
      runAgentTests: "فتح اختباراته",
      runModelTests: "تشغيل اختبارات النموذج",
      howTitle: "تشغيل اختبارات النموذج",
      howBody: "يبدأ المهندس اختبارات النموذج بأداة التقييم، وتظهر النتائج في هذه الصفحة تلقائيًا عند انتهائها.",
      noAgents: "لا يوجد وكلاء بعد.",
      noModels: "لا توجد نماذج ذكاء اصطناعي مسجّلة بعد.",
      error: "تعذّر تحميل نتائج الجودة.",
      engineers: "للمهندسين: السجل الكامل للتجارب موجود ضمن <tools>الأدوات الهندسية › التجارب</tools>.",
      tech: { name: "الاسم التقني", version: "أحدث إصدار", digest: "بصمة الإصدار (SHA-256)", run: "مرجع تشغيل الاختبار: {{suite}}" },
    },
    roles: {
      approver: "المعتمدين",
      "finance-approver": "المعتمدين الماليين",
      "release-approver": "معتمدي الإصدارات",
      reviewer: "المراجعين",
      "case-worker": "موظفي الحالات",
      "case-supervisor": "مشرفي الحالات",
      "channel-agent": "موظفي القنوات",
    },
    ago: "منذ {{time}}",
    list: "{{items}}",
  },
};
